import React, {
	useEffect,
	type ReactNode,
	useLayoutEffect,
	useMemo,
	type ComponentPropsWithoutRef,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, typography } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	runRecalculateSubscribers,
	useElementOffset,
} from '@atlassian/jira-software-fast-virtual/src/services/use-element-offset/index.tsx';
import { useVirtual } from '@atlassian/jira-software-fast-virtual/src/services/use-virtual/index.tsx';
import {
	useShowCompactMode,
	useHasCardExtraFields,
} from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../feature-flags.tsx';
import {
	type CardEntry,
	type CardId,
	type CardListId,
	getCardEntryGroupKey,
	isCardEntryGroup,
} from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { isDragging } from '../../../state/selectors/drag/index.tsx';
import type { RenderRowFn } from './types.tsx';
import { useAutoScrollCard } from './use-auto-scroll-card/index.tsx';
import { useCardHeightCache } from './use-card-height-cache/index.tsx';
import { useEmptyCardListDropTarget } from './use-empty-card-list-drop-target/index.tsx';
import { useOverscanHeight } from './use-overscan-height/index.tsx';

type Props = {
	cardIdWithAboveIccOpen: CardId | null;
	getDefaultRowSize: ({
		isInlineCardCreateOpen,
		numberOfCardsInCardEntry,
		isCompact,
		hideExtraFields,
	}: {
		isInlineCardCreateOpen: boolean;
		numberOfCardsInCardEntry: number;
		isCompact: boolean;
		hideExtraFields?: boolean;
	}) => number;
	defaultCardHeight: number;
	cardEntries: CardEntry[];
	visibleCardAmount: number;
	minimumOffset: number;
	isDropDisabled: boolean;
	cardListId: CardListId;
	emptyList: ReactNode;
	renderCard: RenderRowFn;
	expandedCardGroups: { [p: string]: boolean };
	droppableType: string;
	cardIds: CardId[];
};

export const FastVirtualList = ({
	cardEntries,
	renderCard,
	cardIdWithAboveIccOpen,
	getDefaultRowSize,
	defaultCardHeight,
	cardListId,
	visibleCardAmount,
	emptyList,
	minimumOffset,
	expandedCardGroups,
	cardIds,
}: Props) => {
	const { ref: listElRef, offsetTop } = useElementOffset({ initialOffset: minimumOffset });
	const cardHeightCache = useCardHeightCache();
	const isCompact = useShowCompactMode();

	const cacheOptions = useMemo(
		() => ({
			cache: cardHeightCache,
			getCacheKey: (index: number) => {
				const issueEntry = cardEntries[index];

				if (!issueEntry) {
					return 'none';
				}

				// If its just 1 extra, we know its the ICC
				if (index === cardEntries.length) {
					return 'ICC';
				}

				if (isCardEntryGroup(issueEntry)) {
					return getCardEntryGroupKey(issueEntry, cardListId);
				}

				return issueEntry.cardId;
			},
		}),
		[cardEntries, cardHeightCache, cardListId],
	);

	const isCardListEmpty = cardEntries.length === 0 || visibleCardAmount === 0;

	useEmptyCardListDropTarget({
		elementRef: listElRef,
		isCardListEmpty,
		cardListId,
	});

	const overscanHeight = useOverscanHeight(cardListId, 400);

	const showCardExtraFieldsColumnBasedOnViewSettings =
		isViewSettingAsPanelExpEnabledWithNoExposure()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useHasCardExtraFields()
			: true;

	const { rows, totalSize, reinitialize, isScrolling, scrollTo } = useVirtual({
		rowCount: cardEntries.length,
		overscanHeight,
		offsetTop,
		cacheOptions,
		getDefaultRowSize: (index: number) => {
			const cardEntry = cardEntries[index];
			const cardId = isCardEntryGroup(cardEntry) ? cardEntry?.parentId : cardEntry?.cardId;

			let totalCardsInCardEntry = 1;
			if (isCardEntryGroup(cardEntry)) {
				const isCardGroupExpanded =
					cardEntry.isAttachedToParent === true
						? Boolean(expandedCardGroups[cardEntry.parentId])
						: true;
				if (isCardGroupExpanded) {
					totalCardsInCardEntry = cardEntry.cardIds.length;
				}
			}

			const height = getDefaultRowSize({
				isInlineCardCreateOpen: cardId === cardIdWithAboveIccOpen,
				numberOfCardsInCardEntry: totalCardsInCardEntry,
				isCompact,
				hideExtraFields: !showCardExtraFieldsColumnBasedOnViewSettings,
			});
			return height;
		},
	});

	useAutoScrollCard(cardEntries, scrollTo);

	useEffect(() => {
		reinitialize();
		// ? We need cardIds in here to make sure the card list is recalculated when things change
		// We need `isCompact` as part of dependencies to make sure the card list is recalculated when compact mode is toggled
	}, [
		cardIdWithAboveIccOpen,
		reinitialize,
		cardIds,
		isCompact,
		showCardExtraFieldsColumnBasedOnViewSettings,
	]);

	useLayoutEffect(() => {
		runRecalculateSubscribers();
	}, [cardIds, isCompact]);

	const isCardDragging = useBacklogSelector(isDragging);

	return (
		<div
			ref={listElRef}
			data-test-id={`software-backlog.card-list.id-${cardListId}`}
			data-testid={`software-backlog.card-list.id-${cardListId}`}
		>
			{isCardListEmpty ? (
				<EmptyCardList
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-test-id="software-backlog.card-list.empty-card-list"
					data-testid="software-backlog.card-list.fast-virtual-list.empty-card-list"
					isDragging={isCardDragging}
				>
					{emptyList}
				</EmptyCardList>
			) : (
				<ListContainer
					hide={!isScrolling || isCardDragging}
					defaultCardHeight={defaultCardHeight}
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						position: 'relative',
						height: totalSize,
					}}
				>
					{rows.map((row) => {
						const cardEntry = cardEntries[row.index];
						const cardId = isCardEntryGroup(cardEntry)
							? getCardEntryGroupKey(cardEntry, cardListId)
							: cardEntry?.cardId;

						if (cardId == null) {
							return null;
						}

						return (
							<div
								key={cardId}
								// eslint-disable-next-line jira/react/no-style-attribute
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									position: 'absolute',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									left: 0,
									top: row.top - offsetTop,
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									width: '100%',
								}}
							>
								{renderCard(cardEntry, row, isScrolling)}
							</div>
						);
					})}
				</ListContainer>
			)}
		</div>
	);
};

const EmptyCardListWrapper = (props: ComponentPropsWithoutRef<typeof EmptyCardListExperiment>) => (
	<Box as="div" xcss={emptyCardListWrapperStyles}>
		<Heading size="xxsmall" as="div">
			<EmptyCardListExperiment {...props} />
		</Heading>
	</Box>
);

const emptyCardListWrapperStyles = xcss({
	marginTop: 'space.0',
});

/**
 * This is a background for the lists, which is:
 *
 * * White
 * * Has stripes that look like the cards' borders
 *
 * This is to fool people looking at the list to think cards are rendered when
 * they're not. Since it's only using CSS it can always be displayed & should
 * be reasonably efficient.
 *
 * While dragging the background is disabled.
 */
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListContainerControl = styled.div<{
	hide: boolean;
	defaultCardHeight: number;
}>`
	${({ hide, defaultCardHeight }) =>
		hide
			? ''
			: `
    background-color: ${token('elevation.surface.raised', colors.N0)};
    background-image: repeating-linear-gradient(
        180deg,
        ${token('color.background.neutral.hovered', colors.N30)} 0px,
        ${token('elevation.surface.raised', colors.N0)} 1px,
        ${token('elevation.surface.raised', colors.N0)} ${defaultCardHeight - 1}px,
        ${token('color.background.neutral.hovered', colors.N30)} ${defaultCardHeight}px
    );
    box-shadow: inset 0 0 1px 1px ${token('color.border', colors.N30)};
    `}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ListContainerExperiment = styled2.div<{
	hide: boolean;
	defaultCardHeight: number;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>(({ hide, defaultCardHeight }) =>
	hide
		? ''
		: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				backgroundColor: token('elevation.surface.raised', colors.N0),
				backgroundImage:
					'repeating-linear-gradient' +
					'180deg, ' +
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${token('color.background.neutral.hovered', colors.N30)} 0px, ` +
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${token('elevation.surface.raised', colors.N0)} 1px, ` +
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${token('elevation.surface.raised', colors.N0)} ${defaultCardHeight - 1}px, ` +
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${token('color.background.neutral.hovered', colors.N30)} ${defaultCardHeight}px )`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				boxShadow: `inset 0 0 1px 1px ${token('color.border', colors.N30)}`,
			},
);

const ListContainer = styledComponentWithCondition(
	() => fg('jsw_compiled_migration_tanuki'),
	ListContainerExperiment,
	ListContainerControl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const EmptyCardListControl = styled.div<{ isDragging: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		backgroundColor:
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			props.isDragging
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.selected', colors.B50)
				: undefined,
		border:
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			props.isDragging
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`2px solid ${token('color.border.selected', colors.B300)}`
				: `2px dashed ${token('color.border', '#dfe1e6')}`,
		borderRadius: '3px',
		padding: token('space.150', '12px'),
		textAlign: 'center',
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	typography.h100(),
	{
		fontWeight: 'normal',
		marginTop: token('space.0', '0px'),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const EmptyCardListExperiment = styled2.div<{ isDragging: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isDragging
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.selected', colors.B50)
			: undefined,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.isDragging
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`2px solid ${token('color.border.selected', colors.B300)}`
			: `2px dashed ${token('color.border', '#dfe1e6')}`,
	borderRadius: '3px',
	padding: token('space.150', '12px'),
	textAlign: 'center',
	fontWeight: 'normal',
});

const EmptyCardList = styledComponentWithCondition(
	() => fg('jsw_compiled_migration_tanuki'),
	EmptyCardListWrapper,
	EmptyCardListControl,
);
